/**
 * The parts both transfer popups share (wireframe admin-console-wireframe: "Transfer ownership" and "Transfer
 * everything from a person"), built from existing pieces: the New owner SbSelect over the whole HR pool (people who
 * can't be picked are disabled options with the reason) and the one "Shares and delegations" choice (SbRadio, only when
 * there is someone to carry over or remove). Kept as one file so the two popups don't repeat them.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Flex } from 'antd';
import { SbRadio, SbSpin } from 'uiCore';
// the label over a field and the line under it: the same as the JD page's people pickers (PersonPickerModal)
import { PickerLabelStyled, PickerMessageStyled } from 'Components/JdOwnership/PersonPickerModal.styles';
import {
  CARRY_LABEL, CARRY_OPTIONS, NEW_OWNER_LABEL, NEW_OWNER_PLACEHOLDER, getAffectedLine, getCandidateOptions,
} from 'Utils/ownershipTransfer';
import { FullWidthSelectStyled, InlinePickerLabelStyled } from './OwnershipTransfer.styles';

// error: the list couldn't be loaded (said as ReassignApproverModal does; the select is then disabled)
// rows: the JDs being moved (a greyed-out person's reason names the ones it applies to by Role ID)
export const NewOwnerField = ({ candidates = null, rows = [], loading = false, value = null, onChange,
  disabled = false, helpBelow = null, error = null }) => (
  <div>
    <PickerLabelStyled>{NEW_OWNER_LABEL}</PickerLabelStyled>
    {loading ? <SbSpin size="small" /> : (
      <FullWidthSelectStyled
        options={getCandidateOptions(candidates || {}, rows)}
        value={value || undefined}
        onChange={(v) => onChange(v || null)}
        placeholder={NEW_OWNER_PLACEHOLDER}
        showSearch
        optionFilterProp="label"
        allowClear
        disabled={disabled || !!error}
        // rendered in the page body (antd's default), so it opens upwards when there is no room below
        aria-label={NEW_OWNER_LABEL}
      />
    )}
    {error ? <PickerMessageStyled data-testid="owner-error">{error}</PickerMessageStyled>
      : helpBelow && <PickerMessageStyled data-testid="owner-help">{helpBelow}</PickerMessageStyled>}
  </div>
);

NewOwnerField.propTypes = {
  candidates: PropTypes.shape({ candidates: PropTypes.array, excluded: PropTypes.array }),
  rows: PropTypes.arrayOf(PropTypes.shape({ job_id: PropTypes.string, sb_requisition_number: PropTypes.string })),
  loading: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  helpBelow: PropTypes.string,
  error: PropTypes.string,
};

// One row: the label, then Carry over / Remove side by side, and who it affects under it. Shown ONLY when the JDs have
// a delegate or shared people to carry over or remove; otherwise there is nothing to choose (nothing rendered)
export const CarryChoice = ({ value, onChange, affectedNames = [] }) => {
  const affected = getAffectedLine(affectedNames);
  if (!affected) return null;
  return (
    <div>
      <Flex wrap align="center" gap={16} role="radiogroup" aria-label={CARRY_LABEL}>
        <InlinePickerLabelStyled>{CARRY_LABEL}</InlinePickerLabelStyled>
        {CARRY_OPTIONS.map((option) => (
          <SbRadio key={option.value} value={option.value} checked={value === option.value}
            onChange={() => onChange(option.value)} aria-label={option.title}>
            {option.title}
          </SbRadio>
        ))}
      </Flex>
      <PickerMessageStyled data-testid="carry-affected">{affected}</PickerMessageStyled>
    </div>
  );
};

CarryChoice.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  affectedNames: PropTypes.arrayOf(PropTypes.string),
};
